import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { clearStoredToken, getStoredToken, isAdminRole, setStoredToken } from "./token";

export type AuthUser = {
  user_id: number;
  username: string;
  role: string;
  player_id: number | null;
  firstName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
};

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function apiBaseUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }
  return base.replace(/\/$/, "");
}

async function fetchMe(token: string): Promise<AuthUser> {
  const response = await fetch(`${apiBaseUrl()}/auth/me.php`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = (await response.json().catch(() => ({}))) as {
    user?: AuthUser;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(typeof body.message === "string" ? body.message : "Session expired");
  }
  if (!body.user) {
    throw new Error("Invalid auth response");
  }
  return body.user;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    void fetchMe(token)
      .then((me) => {
        if (!isAdminRole(me.role)) {
          clearStoredToken();
          setUser(null);
          return;
        }
        setUser(me);
      })
      .catch(() => {
        clearStoredToken();
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await fetch(`${apiBaseUrl()}/auth/login.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const body = (await response.json().catch(() => ({}))) as {
      token?: string;
      user?: AuthUser;
      message?: string;
    };
    if (!response.ok) {
      throw new Error(typeof body.message === "string" ? body.message : "Login failed");
    }
    if (!body.token || !body.user) {
      throw new Error("Invalid login response");
    }
    if (!isAdminRole(body.user.role)) {
      throw new Error("Admin or tournament director access required.");
    }
    setStoredToken(body.token);
    setUser(body.user);
  }, []);

  const logout = useCallback(async () => {
    const token = getStoredToken();
    if (token) {
      try {
        await fetch(`${apiBaseUrl()}/auth/logout.php`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Best-effort revoke.
      }
    }
    clearStoredToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, login, logout }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
